import React, { useState } from 'react';
import { Modal, Button } from '../common';
import { ShieldCheck, Bell, Download, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

const PrivacyConsentModal = ({ show, onAccept }) => {
  const [notifConsent, setNotifConsent] = useState(true);
  const [loading, setLoading]           = useState(false);
  const [tab, setTab]                   = useState('policy'); // 'policy' | 'rights'

  const handleAccept = async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/privacy/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notifications_consent: notifConsent }),
      });
      onAccept({ notifications_consent: notifConsent });
    } catch {
      // fallback — acepta igual para no bloquear al usuario
      onAccept({ notifications_consent: notifConsent });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onClose={null}
      title="Política de Privacidad y Protección de Datos"
      maxWidth="2xl"
    >
      {/* Tabs */}
      <div className="flex border-b border-slate-600 mb-4">
        {[
          { key: 'policy', label: 'Política de Privacidad' },
          { key: 'rights', label: 'Tus Derechos (Ley 21.719)' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? 'border-cyan-500 text-cyan-400'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'policy' && (
        <div className="space-y-4 text-sm text-slate-300 max-h-72 overflow-y-auto pr-1">
          <div className="flex gap-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
            <ShieldCheck className="text-cyan-400 shrink-0 mt-0.5" size={18} />
            <p>
              Sistema PRT trata tus datos personales para brindarte el servicio de monitoreo de
              mantenimiento industrial. Cumplimos con la <strong className="text-white">Ley N° 21.719</strong> de
              Protección de Datos Personales de Chile.
            </p>
          </div>

          <div>
            <p className="font-semibold text-white mb-1">¿Qué datos recopilamos?</p>
            <ul className="list-disc list-inside space-y-1 text-slate-400">
              <li>Nombre y email para identificación y acceso</li>
              <li>Actividad dentro del sistema (fallas, calibraciones, activos)</li>
              <li>Registros de auditoría de seguridad (IP, acciones)</li>
              <li>Suscripciones a notificaciones push (si las activas)</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-white mb-1">¿Con quién compartimos tus datos?</p>
            <ul className="list-disc list-inside space-y-1 text-slate-400">
              <li><strong className="text-slate-300">Railway</strong> — hosting del servidor (Chile/US)</li>
              <li><strong className="text-slate-300">Neon / Cloudflare R2</strong> — base de datos y archivos</li>
              <li><strong className="text-slate-300">Resend</strong> — envío de correos transaccionales</li>
              <li>No vendemos ni cedemos datos a terceros con fines comerciales</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-white mb-1">Retención</p>
            <p className="text-slate-400">
              Conservamos tus datos mientras tu cuenta esté activa. Puedes solicitar su eliminación
              en cualquier momento desde tu perfil.
            </p>
          </div>

          <div>
            <p className="font-semibold text-white mb-1">Responsable del tratamiento</p>
            <p className="text-slate-400">
              Systech Chile SpA · contacto: sistemademonitoreoprt@gmail.com
            </p>
          </div>
        </div>
      )}

      {tab === 'rights' && (
        <div className="space-y-3 text-sm text-slate-300 max-h-72 overflow-y-auto pr-1">
          <p className="text-slate-400">
            Bajo la Ley 21.719 tienes los siguientes derechos sobre tus datos personales:
          </p>
          {[
            {
              icon: <Download size={16} className="text-cyan-400 shrink-0 mt-0.5" />,
              title: 'Derecho de Acceso',
              desc: 'Solicita una copia de todos tus datos desde tu perfil → "Mis Datos".',
            },
            {
              icon: <Trash2 size={16} className="text-red-400 shrink-0 mt-0.5" />,
              title: 'Derecho de Eliminación',
              desc: 'Solicita la eliminación de tu cuenta y anonimización de tus datos desde "Mis Datos".',
            },
            {
              icon: <ShieldCheck size={16} className="text-green-400 shrink-0 mt-0.5" />,
              title: 'Derecho de Rectificación',
              desc: 'Puedes actualizar tu nombre e información de perfil en cualquier momento.',
            },
            {
              icon: <Bell size={16} className="text-yellow-400 shrink-0 mt-0.5" />,
              title: 'Derecho a Revocar Consentimiento',
              desc: 'Puedes desactivar las notificaciones en cualquier momento desde tu perfil.',
            },
          ].map(r => (
            <div key={r.title} className="flex gap-3 bg-slate-700/50 rounded-lg p-3">
              {r.icon}
              <div>
                <p className="font-semibold text-white">{r.title}</p>
                <p className="text-slate-400">{r.desc}</p>
              </div>
            </div>
          ))}
          <p className="text-slate-500 text-xs pt-1">
            Para ejercer tus derechos también puedes escribirnos a: sistemademonitoreoprt@gmail.com
          </p>
        </div>
      )}

      {/* Consentimiento de notificaciones */}
      <div className="mt-4 flex items-start gap-3 bg-slate-700/50 rounded-lg p-3">
        <input
          id="notif-consent"
          type="checkbox"
          checked={notifConsent}
          onChange={e => setNotifConsent(e.target.checked)}
          className="mt-1 accent-cyan-500 cursor-pointer"
        />
        <label htmlFor="notif-consent" className="text-sm text-slate-300 cursor-pointer">
          <Bell size={14} className="inline mr-1 text-cyan-400" />
          Acepto recibir notificaciones por correo sobre fallas, alertas y reportes diarios del sistema.{' '}
          <span className="text-slate-500">(Puedes revocar este consentimiento en cualquier momento)</span>
        </label>
      </div>

      <div className="mt-5 flex justify-end">
        <Button
          onClick={handleAccept}
          disabled={loading}
          className="bg-cyan-600 hover:bg-cyan-700 text-white px-6"
        >
          {loading ? 'Registrando...' : 'Acepto la Política de Privacidad'}
        </Button>
      </div>

      <p className="mt-3 text-xs text-slate-500 text-center">
        Al continuar aceptas nuestros términos de tratamiento de datos según la Ley 21.719.
        Esta aceptación quedará registrada con fecha y hora.
      </p>
    </Modal>
  );
};

export default PrivacyConsentModal;
